import { z } from "zod";

export const emailSchema = z
  .string()
  .trim()
  .min(1, "Email is required")
  .email("Enter a valid email")
  .max(255);

export const passwordSchema = z.string().min(8, "At least 8 characters").max(72, "Too long");

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Password is required").max(72),
});

export const registerSchema = z
  .object({
    fullName: z.string().trim().min(2, "Enter your name").max(100),
    email: emailSchema,
    password: passwordSchema,
    confirm: z.string(),
    company: z.string().trim().max(120).optional().or(z.literal("")),
    terms: z.literal(true, {
      errorMap: () => ({ message: "You must accept the terms to continue" }),
    }),
  })
  .refine((d) => d.password === d.confirm, {
    message: "Passwords do not match",
    path: ["confirm"],
  });

export const forgotSchema = z.object({ email: emailSchema });

export const resetSchema = z
  .object({ password: passwordSchema, confirm: z.string() })
  .refine((d) => d.password === d.confirm, {
    message: "Passwords do not match",
    path: ["confirm"],
  });

/**
 * Normalize an unknown thrown value into a human-readable string.
 * Handles Error instances, Supabase AuthError / PostgrestError-shaped
 * plain objects ({ message, error_description, msg, code, details, hint }),
 * strings, and unknown values — never returns "[object Object]".
 */
export function toErrorMessage(err: unknown): string {
  if (err == null) return "";
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message || err.name || "Error";
  if (typeof err === "object") {
    const e = err as Record<string, unknown>;
    const candidates = [
      e.message,
      e.error_description,
      e.msg,
      e.error,
      e.details,
      e.hint,
      e.statusText,
    ];
    for (const c of candidates) {
      if (typeof c === "string" && c.trim()) return c;
    }
    try {
      const json = JSON.stringify(err);
      if (json && json !== "{}") return json;
    } catch {
      /* ignore */
    }
  }
  return "Something went wrong. Please try again.";
}

export function friendlyAuthError(input?: unknown): string {
  const message = typeof input === "string" ? input : toErrorMessage(input);
  if (!message) return "Something went wrong. Please try again.";
  const m = message.toLowerCase();
  if (m.includes("invalid login") || m.includes("invalid credentials"))
    return "Email or password is incorrect.";
  if (m.includes("email not confirmed"))
    return "Please confirm your email address before signing in.";
  if (m.includes("already registered") || m.includes("already exists"))
    return "An account with this email already exists. Try signing in.";
  if (m.includes("rate")) return "Too many attempts. Please wait a moment.";
  if (m.includes("network") || m.includes("fetch")) return "Network error. Check your connection.";
  return message;
}
